import type { Schemas } from '@argzombie/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { errorText, ok, useApi } from '../lib/api';
import { useRunScope } from '../lib/characters';
import { when } from '../lib/datetime';
import { checkInOutcome } from '../lib/playerSupport';

type BaseRow = Schemas['AdminBaseRow'];
type CheckIn = Schemas['CheckInRow'];

const th = 'px-2 py-1 text-left';
const td = 'px-2 py-1';
const btn =
  'rounded border-2 border-ink px-2 py-0.5 text-xs font-semibold hover:bg-caption/40 disabled:opacity-40';

/** Status plates: cyan = active, red = revoked, plain = expired or abandoned (history). */
const statusCls: Record<string, string> = {
  active: 'bg-benday',
  revoked: 'bg-blam text-white',
};

const sourceLabel: Record<string, string> = {
  phone_unpack: 'phone (unpack)',
  make_home: 'make this my home',
  phone_stash: 'phone (stash)',
  web_qr: 'web QR',
  home_set: 'home set',
};

/**
 * A player's bases (homebase and camps, any status) and their recent check-ins (ADR 0030 §10), from
 * `GET /admin/players/{id}`. Admins can revoke an active base or void a check-in, each with a reason and a confirm;
 * the player query is refetched afterwards (counters are recomputed on the server).
 */
export function PlayerBasesSection({
  playerId,
  bases,
  checkIns,
}: {
  playerId: string;
  bases: BaseRow[];
  checkIns: CheckIn[];
}) {
  const [open, setOpen] = useState<string | null>(null);
  const baseName = new Map(bases.map((b) => [b.id, `${b.name} (${b.kind})`]));
  const toggle = (key: string) => setOpen(open === key ? null : key);

  return (
    <>
      <section className="overflow-x-auto rounded border-2 border-ink bg-white p-4 lg:col-span-2">
        <h2 className="font-semibold">Bases</h2>
        {bases.length === 0 ? (
          <p className="text-sm">No bases.</p>
        ) : (
          <table className="mt-2 w-full text-xs">
            <thead className="text-bruise">
              <tr>
                <th className={th}>Kind</th>
                <th className={th}>Name</th>
                <th className={th}>Status</th>
                <th className={th}>Check-ins</th>
                <th className={th}>Where</th>
                <th className={th}>Dates</th>
                <th className={th} />
              </tr>
            </thead>
            <tbody>
              {bases.map((b) => (
                <Fragment key={b.id}>
                  <tr className="border-t border-newsprint align-top">
                    <td className={`${td} font-semibold`}>
                      {b.kind === 'camp' ? `outpost ${b.slot}` : b.kind}
                    </td>
                    <td className={td}>{b.name}</td>
                    <td className={td}>
                      <span
                        className={`rounded px-1.5 py-0.5 font-semibold ${statusCls[b.status] ?? 'bg-newsprint'}`}
                      >
                        {b.status}
                      </span>
                      {b.endedReason && <div className="text-bruise">{b.endedReason}</div>}
                    </td>
                    <td className={td}>
                      {b.countedCheckIns} counted · {b.distinctDays} days
                      {b.lastCountedAt && (
                        <div className="text-bruise">last {when(b.lastCountedAt)}</div>
                      )}
                    </td>
                    <td className={`${td} font-mono`}>
                      {b.lat.toFixed(5)}, {b.lon.toFixed(5)}
                    </td>
                    <td className={`${td} font-mono`}>
                      <div>created {when(b.createdAt)}</div>
                      {b.droppedAt && <div>dropped {when(b.droppedAt)}</div>}
                      {b.promotedAt && <div>promoted {when(b.promotedAt)}</div>}
                      {b.endedAt && <div>ended {when(b.endedAt)}</div>}
                    </td>
                    <td className={td}>
                      {b.status === 'active' && (
                        <button
                          type="button"
                          className={btn}
                          onClick={() => toggle(`base:${b.id}`)}
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                  {open === `base:${b.id}` && (
                    <tr className="bg-newsprint/40">
                      <td colSpan={7} className="px-2 py-2">
                        <ReasonEditor
                          playerId={playerId}
                          label={`Revoke reason for ${b.name}`}
                          button="Revoke base"
                          note={
                            b.kind === 'home'
                              ? 'Ends the homebase: the player has no home until they set a new one.'
                              : 'Ends the outpost; it keeps its history and frees its slot.'
                          }
                          confirmText={`Revoke ${b.kind} "${b.name}"?`}
                          send={(client, reason) =>
                            client.POST('/admin/bases/{id}/revoke', {
                              params: { path: { id: b.id } },
                              body: { reason },
                            })
                          }
                          onDone={() => setOpen(null)}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="overflow-x-auto rounded border-2 border-ink bg-white p-4 lg:col-span-2">
        <h2 className="font-semibold">Recent check-ins</h2>
        {checkIns.length === 0 ? (
          <p className="text-sm">No check-ins.</p>
        ) : (
          <table className="mt-2 w-full text-xs">
            <thead className="text-bruise">
              <tr>
                <th className={th}>When</th>
                <th className={th}>Base</th>
                <th className={th}>Source</th>
                <th className={th}>Outcome</th>
                <th className={th}>Distance</th>
                <th className={th}>Accuracy</th>
                <th className={th}>Flag</th>
                <th className={th}>Void</th>
                <th className={th} />
              </tr>
            </thead>
            <tbody>
              {checkIns.map((c) => (
                <Fragment key={c.id}>
                  <tr
                    className={`border-t border-newsprint align-top ${c.voidedAt ? 'text-bruise line-through' : ''}`}
                  >
                    <td className={`${td} font-mono`}>{when(c.at)}</td>
                    <td className={td}>{c.baseId ? (baseName.get(c.baseId) ?? c.baseId) : '-'}</td>
                    <td className={td}>{sourceLabel[c.source] ?? c.source}</td>
                    <td className={`${td} ${c.counted ? 'font-semibold' : ''}`}>
                      {checkInOutcome(c)}
                    </td>
                    <td className={`${td} font-mono`}>
                      {c.distanceM != null ? `${Math.round(c.distanceM)} m` : '-'}
                    </td>
                    <td className={`${td} font-mono`}>
                      {c.accuracyM != null ? `±${Math.round(c.accuracyM)} m` : '-'}
                    </td>
                    <td className={`${td} ${c.flag ? 'text-blam' : ''}`}>{c.flag ?? ''}</td>
                    <td className={td}>
                      {c.voidedAt
                        ? `void ${when(c.voidedAt)}${c.voidReason ? `: ${c.voidReason}` : ''}`
                        : ''}
                    </td>
                    <td className={td}>
                      {!c.voidedAt && (
                        <button type="button" className={btn} onClick={() => toggle(`ci:${c.id}`)}>
                          Void
                        </button>
                      )}
                    </td>
                  </tr>
                  {open === `ci:${c.id}` && (
                    <tr className="bg-newsprint/40">
                      <td colSpan={9} className="px-2 py-2">
                        <ReasonEditor
                          playerId={playerId}
                          label={`Void reason for check-in ${c.id}`}
                          button="Void check-in"
                          note="It stops counting toward its base's promotion and expiry."
                          confirmText={`Void check-in ${c.id}?`}
                          send={(client, reason) =>
                            client.POST('/admin/checkins/{id}/void', {
                              params: { path: { id: c.id } },
                              body: { reason },
                            })
                          }
                          onDone={() => setOpen(null)}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

type Client = ReturnType<typeof useApi>['client'];

/** A reason + confirm + POST form, like the redemption void. Refetches the player on success. */
function ReasonEditor({
  playerId,
  label,
  button,
  note,
  confirmText,
  send,
  onDone,
}: {
  playerId: string;
  label: string;
  button: string;
  note: string;
  confirmText: string;
  send: (
    client: Client,
    reason: string,
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
  onDone: () => void;
}) {
  const { session } = useApi();
  const { client } = useRunScope();
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const run = useMutation({
    mutationFn: async () => ok(await send(client, reason.trim())),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-player', playerId] });
      onDone();
    },
    onError: (e) => setProblem(errorText(e)),
  });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!reason.trim()) return setProblem('A reason is required.');
        if (!confirm(confirmText)) return;
        run.mutate();
      }}
      className="flex flex-wrap items-center gap-2 text-sm"
    >
      {!session.isAdmin && <span className="text-xs text-bruise">This is for Admins.</span>}
      <input
        aria-label={label}
        placeholder="reason (required)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        className="w-80 max-w-full rounded border-2 border-ink bg-white px-2 py-1"
      />
      <button
        type="submit"
        disabled={run.isPending}
        className="rounded border-2 border-ink bg-blam px-3 py-1 text-sm font-semibold text-white disabled:opacity-50"
      >
        {button}
      </button>
      <span className="text-xs text-bruise">{note}</span>
      {problem && (
        <p className="w-full text-blam" role="alert">
          {problem}
        </p>
      )}
    </form>
  );
}
